import React, { useState, useRef, useEffect } from 'react';
import { Switch, Button, Col, Drawer, Form, Input, Row, Select, Space, message, InputNumber, DatePicker } from 'antd';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { insert, update, getById } from '../../../../apis/OdooTimeSheetApi';
import { getList as getAnaliticas } from '../../../../apis/OdooAnaliticaApi';
import { getEmpleadosActivos } from '../../../../apis/OdooApi';
import moment from 'moment';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const App = ({ title, id, open, finish }) => {
    const [form] = Form.useForm();
    const [ID, setID] = useState(id);
    const [modified, setModified] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [empleados, setEmpleados] = useState([]);
    const [analiticas, setAnaliticas] = useState([]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (!values) return;
        } catch (errorInfo) {
            console.log('Failed:', errorInfo);
            return;
        }

        let success = false;
        const fields = form.getFieldsValue();

        if (fields.date) {
            fields.date = fields.date.format('YYYY-MM-DD');  
        }

        if (ID > 0) {
            const payload = {
                id: ID,
                name: fields.name,
                date: fields.date,
                unit_amount: fields.unit_amount,
                odoo_analitica_id: fields.odoo_analitica_id
            };
            const response = await update(ID, payload);
            success = response.ok;
            if (response.ok) {
                message.success('Se actualizó correctamente el timesheet!');
            }
        } else {
            const payload = {
                ...fields,
                odoo_employee_id: fields.odoo_employee_id
            };
            const response = await insert(payload);
            success = response.ok;
            if (response.ok) {
                setID(response.payload);
                message.success('Se guardó correctamente el timesheet!');
            }
        }

        if (success) {
            setModified(true);
            onClose(true);
        }
    };

    const getAnaliticasOdoo = async () => {
        const response = await getAnaliticas();
        if (response.ok) {
            let items = response.payload;
            setAnaliticas(items);
        }
    };

    const onClose = (reload) => {
        finish(reload ?? modified);
        setInitialValues(null);
    };

    const effectRan = useRef(false);
    useEffect(() => {
        if (!effectRan.current) {
            (async () => {
                await getEmpleadosOdoo();
                await getAnaliticasOdoo();
                setModified(false);
            })();
        }
        return () => effectRan.current = true;
    }, []);

    useEffect(() => {
        if (empleados.length > 0 && analiticas.length > 0 && ID > 0 && !initialValues) {
            (async () => {
                await refreshData();
            })();
        } else if (empleados.length > 0 && analiticas.length > 0 && !ID && !initialValues) {
            setInitialValues({
                unit_amount: 0,
                name: '',
                date: dayjs()
            });
        }
    }, [empleados, analiticas]);

    const refreshData = async () => {
        const response = await getById(ID);
        if (response.ok) {
            let item = response.payload;
            if (item.date) {
                item.date = dayjs(item.date);
            }
            if (item.empleado && item.empleado.odoo_id) {
                item.odoo_employee_id = item.empleado.odoo_id;
            }
            setInitialValues(item);
        }
    };

    const getEmpleadosOdoo = async () => {
        const response = await getEmpleadosActivos({ nomina_need_timesheets: true });
        if (response.ok) {
            let items = response.payload;
            setEmpleados(items);
        }
    };

    return (
        <Drawer
            title={title}
            width={720}
            placement={"right"}
            onClose={onClose}
            open={open}
            closable={true}
            extra={<Space>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleOk} type="primary">
                    Guardar
                </Button>
            </Space>}
        >
            {
                initialValues &&
                <Form
                    layout="vertical"
                    form={form}
                    requiredMark
                    autoComplete="off"
                    initialValues={initialValues}
                    onFinish={handleOk}>
                    <Row gutter={16}>
                        <Col md={{ span: 24 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="odoo_employee_id"
                                label="Empleado"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Selecciona un empleado',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Por favor selecciona un empleado"
                                    disabled={ID > 0}
                                >
                                    {
                                        empleados.map((item) => <Option key={item.odoo_id} value={item.odoo_id}>{item.text}</Option>)
                                    }
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 24 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="name"
                                label="Descripción"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor ingresa una descripción',
                                    },
                                ]}
                            >
                                <TextArea
                                    placeholder="Descripción del timesheet..."
                                    rows={3}
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 24 }} sm={{ span: 24 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="odoo_analitica_id"
                                label="Analítica"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Selecciona una analítica',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Por favor selecciona una analítica"
                                    disabled={ID > 0}
                                >
                                    {
                                        analiticas.map((item) => <Option key={item.id} value={item.id}>{item.name}</Option>)
                                    }
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="date"
                                label="Fecha"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor selecciona la fecha',
                                    },
                                ]}
                            >
                                <DatePicker
                                    placeholder="Selecciona la fecha"
                                    style={{ width: '100%' }}
                                    disabled={ID > 0}
                                    format="DD/MM/YYYY"
                                />
                            </Form.Item>
                        </Col>
                        <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="unit_amount"
                                label="Cantidad de Horas"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Por favor ingresa la cantidad de horas',
                                    },
                                ]}
                            >
                                <InputNumber
                                    placeholder="Horas trabajadas"
                                    style={{ width: '100%' }}
                                    min={0}
                                    step={0.5}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            }
        </Drawer>
    );
};

export default App;